// src/SignupSuccessPage.js
import { useState, useEffect } from 'react';
import { getApiBase } from './apiBase';

const API_BASE = getApiBase();

const SignupSuccessPage = ({ onLoginSuccess }) => {
  const [step, setStep] = useState('loading');
  const [sessionData, setSessionData] = useState(null);
  const [sessionId, setSessionId] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    setSessionId(sessionId);
    if (!sessionId) {
      setStep('error');
      setError('Invalid signup link. Please contact support@mytoolnow.com');
      return;
    }
    fetch(`${API_BASE}/api/subscribe/session/${sessionId}`)
      .then(res => res.json())
      .then(data => {
        if (data.ok) {
          setSessionData(data);
          setStep('setpassword');
        } else {
          setError(data.error || 'Account not found. Please contact support@mytoolnow.com');
          setStep('error');
        }
      })
      .catch(() => {
        setError('Network error. Please contact support@mytoolnow.com');
        setStep('error');
      });
  }, []);

  const handleSetPassword = async () => {
    setError('');
    if (!password || password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/subscribe/set-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          password
        })
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Failed to set password. Please contact support@mytoolnow.com');
        return;
      }
      localStorage.setItem('auth_token', data.token);
      localStorage.setItem('tenant_id', String(data.user.tenant_id));
      localStorage.setItem('tenantId', String(data.user.tenant_id));
      localStorage.setItem('user_email', data.user.email);
      localStorage.setItem('user_role', data.user.role);
      localStorage.setItem('user_id', String(data.user.id));
      setStep('done');
      setTimeout(() => {
        if (onLoginSuccess) onLoginSuccess(data.user);
      }, 2000);
    } catch (e) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    page: {
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0b1a3a 0%, #000 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 20,
      boxSizing: 'border-box'
    },
    card: {
      background: '#111827',
      border: '3px solid #E8761D',
      padding: 40,
      maxWidth: 480,
      width: '100%',
      boxSizing: 'border-box'
    },
    logo: {
      textAlign: 'center',
      marginBottom: 24
    },
    title: {
      fontWeight: 900,
      fontSize: 26,
      color: '#ffffff',
      textAlign: 'center',
      marginBottom: 8,
      fontFamily: 'Arial, sans-serif',
      textTransform: 'uppercase',
      letterSpacing: 1
    },
    subtitle: {
      fontWeight: 700,
      fontSize: 14,
      color: '#E8761D',
      textAlign: 'center',
      marginBottom: 28,
      fontFamily: 'Arial, sans-serif',
      textTransform: 'uppercase',
      letterSpacing: 2
    },
    label: {
      fontWeight: 900,
      fontSize: 12,
      color: '#aaaaaa',
      textTransform: 'uppercase',
      letterSpacing: 1,
      marginBottom: 6,
      fontFamily: 'Arial, sans-serif',
      display: 'block'
    },
    input: {
      width: '100%',
      padding: '12px 14px',
      background: '#1f2937',
      border: '2px solid #374151',
      color: '#ffffff',
      fontSize: 16,
      fontFamily: 'Arial, sans-serif',
      boxSizing: 'border-box',
      marginBottom: 16,
      outline: 'none'
    },
    btn: {
      width: '100%',
      padding: '14px',
      background: '#E8761D',
      color: '#000000',
      border: 'none',
      fontWeight: 900,
      fontSize: 16,
      textTransform: 'uppercase',
      letterSpacing: 2,
      cursor: 'pointer',
      fontFamily: 'Arial, sans-serif',
      marginTop: 8
    },
    btnDisabled: {
      opacity: 0.5,
      cursor: 'not-allowed'
    },
    errorBox: {
      background: '#7f1d1d',
      border: '2px solid #ef4444',
      color: '#fca5a5',
      padding: '12px 16px',
      fontWeight: 700,
      fontSize: 14,
      fontFamily: 'Arial, sans-serif',
      marginBottom: 16
    },
    successTitle: {
      textAlign: 'center',
      color: '#E8761D',
      fontWeight: 900,
      fontSize: 22,
      fontFamily: 'Arial, sans-serif',
      textTransform: 'uppercase',
      letterSpacing: 1
    },
    successSub: {
      textAlign: 'center',
      color: '#aaaaaa',
      fontWeight: 700,
      fontSize: 14,
      fontFamily: 'Arial, sans-serif',
      marginTop: 12
    },
    loadingText: {
      textAlign: 'center',
      color: '#E8761D',
      fontWeight: 900,
      fontSize: 16,
      fontFamily: 'Arial, sans-serif',
      textTransform: 'uppercase',
      letterSpacing: 2
    },
    showHideBtn: {
      position: 'absolute',
      right: 12,
      top: 12,
      background: 'none',
      border: 'none',
      color: '#aaaaaa',
      cursor: 'pointer',
      fontSize: 13,
      fontWeight: 700,
      fontFamily: 'Arial, sans-serif',
      textTransform: 'uppercase'
    },
    supportLink: {
      color: '#E8761D',
      fontWeight: 700,
      fontFamily: 'Arial, sans-serif'
    }
  };

  const renderLoading = () => (
    <div style={styles.loadingText}>Verifying your payment...</div>
  );

  const renderError = () => (
    <div>
      <div style={styles.title}>Something went wrong</div>
      <div style={styles.errorBox}>{error}</div>
      <div style={{ textAlign: 'center', marginTop: 16 }}>
        <span style={styles.supportLink}>support@mytoolnow.com</span>
      </div>
    </div>
  );

  const renderSetPassword = () => (
    <div>
      <div style={styles.title}>Welcome to MyTool NOW!</div>
      <div style={styles.subtitle}>Set your password to get started</div>
      {error ? <div style={styles.errorBox}>{error}</div> : null}
      {sessionData && sessionData.email && (
        <div style={{ marginBottom: 16 }}>
          <span style={styles.label}>Email</span>
          <div style={{ ...styles.input, color: '#aaaaaa', cursor: 'default' }}>
            {sessionData.email}
          </div>
        </div>
      )}
      <label style={styles.label}>Password</label>
      <div style={{ position: 'relative', marginBottom: 0 }}>
        <input
          style={{ ...styles.input, paddingRight: 64 }}
          type={showPassword ? 'text' : 'password'}
          value={password}
          onChange={e => setPassword(e.target.value)}
          placeholder="Min 8 characters"
          disabled={loading}
        />
        <button onClick={() => setShowPassword(s => !s)} style={styles.showHideBtn}>
          {showPassword ? 'Hide' : 'Show'}
        </button>
      </div>
      <label style={styles.label}>Confirm Password</label>
      <input
        style={styles.input}
        type={showPassword ? 'text' : 'password'}
        value={confirmPassword}
        onChange={e => setConfirmPassword(e.target.value)}
        placeholder="Re-enter password"
        disabled={loading}
        onKeyDown={e => e.key === 'Enter' && handleSetPassword()}
      />
      <button
        style={{ ...styles.btn, ...(loading ? styles.btnDisabled : {}) }}
        onClick={handleSetPassword}
        disabled={loading}
      >
        {loading ? 'Setting up your account...' : 'Activate My Account'}
      </button>
    </div>
  );

  const renderDone = () => (
    <div>
      <div style={styles.successTitle}>Account Activated!</div>
      <div style={styles.successSub}>Welcome to MyTool NOW! Your account is ACTIVE! Check your email inbox or spam folder that you subscribed with, for Log-In information. Enjoy MyTool NOW, your new Business Management TOOL and Partner!!</div>
    </div>
  );

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.logo}>
          <img src="/mtnlogo.png" alt="MyTool NOW" style={{ width: 180, height: 'auto' }} />
        </div>
        {step === 'loading' && renderLoading()}
        {step === 'error' && renderError()}
        {step === 'setpassword' && renderSetPassword()}
        {step === 'done' && renderDone()}
      </div>
    </div>
  );
};

export default SignupSuccessPage;